/**
 * PluginConfigPage
 *
 * Page for configuring a specific plugin for an agent. Renders a dynamic form
 * based on the plugin's JSON Schema and handles saving configuration.
 *
 * Validates: Requirements 5.1, 5.2, 5.3, 5.4, 5.5
 * - Requirement 5.1: WHEN an operator selects a plugin to configure, THE Console
 *   SHALL render a dynamic form based on the plugin's configuration schema.
 * - Requirement 5.2: THE Console SHALL validate plugin configuration inputs against
 *   the schema before saving.
 * - Requirement 5.3: IF configuration validation fails, THEN THE Console SHALL
 *   display field-level error messages.
 * - Requirement 5.4: THE Console SHALL provide default values for optional
 *   configuration fields.
 * - Requirement 5.5: WHEN configuration is saved successfully, THE Console SHALL
 *   display a confirmation message.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, TENANT_TOKEN_KEY } from '../../api/client';
import { Card, Button, Badge } from '../../design-system';
import { tokens } from '../../design-system/tokens';
import {
  DynamicSchemaForm,
  type JSONSchema7,
  type FieldError,
  validateForm,
  applyDefaults,
} from './DynamicSchemaForm';
import { PatternEditor, type IntentConfig } from './PatternEditor';
import { StalenessIndicator } from './StalenessIndicator';
import { PatternDiffView } from './PatternDiffView';
import { ImpactoIntelligencePanel } from './ImpactoIntelligencePanel';
import { LeadQualificationSettings } from './LeadQualificationSettings';

// ============================================
// TYPES
// ============================================

/** Plugin metadata from the registry */
interface PluginMetadata {
  id: string;
  name: string;
  description: string;
  version: string;
  category: string;
  icon?: string;
  configSchema?: JSONSchema7;
}

/** Agent plugin configuration */
interface AgentPluginConfig {
  id: string;
  config: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** API response for plugin details */
interface PluginDetailResponse {
  plugin: PluginMetadata;
  agentConfig: AgentPluginConfig | null;
  isEnabled: boolean;
}

/** Agent basic info */
interface Agent {
  id: string;
  name: string;
}

/** Response from the generate-patterns endpoint (uses `intentPatterns` key) */
interface GeneratedIntentConfigResponse {
  version: string;
  generatedAt: string;
  intentPatterns: Record<string, import('./PatternEditor').IntentPatternDef>;
}

/**
 * Converts a GeneratedIntentConfig response (with `intentPatterns` key)
 * to an IntentConfig (with `patterns` key) for use in the UI and PUT endpoint.
 */
function toIntentConfig(generated: GeneratedIntentConfigResponse): IntentConfig {
  return {
    version: generated.version,
    generatedAt: generated.generatedAt,
    patterns: generated.intentPatterns,
  };
}

/** Plugins whose intent patterns are edited in the console (not image-recognition / rest-catalog). */
function pluginIdSupportsIntentPatternsUi(pluginId: string): boolean {
  const id = pluginId.toLowerCase();
  return id.includes('calendar') || id.includes('s3');
}

/** Keep only keys declared in the plugin JSON Schema (intentPatterns are edited separately). */
function pickFormValuesFromSchema(
  config: Record<string, unknown>,
  schema: JSONSchema7,
): Record<string, unknown> {
  const properties = schema.properties ?? {};
  const picked: Record<string, unknown> = {};
  for (const key of Object.keys(properties)) {
    if (config[key] !== undefined) {
      picked[key] = config[key];
    }
  }
  return picked;
}

// ============================================
// ICONS
// ============================================

function IconArrowLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function PluginConfigPage() {
  const { agentId, pluginId } = useParams<{ agentId: string; pluginId: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem(TENANT_TOKEN_KEY);

  // State
  const [agent, setAgent] = useState<Agent | null>(null);
  const [plugin, setPlugin] = useState<PluginMetadata | null>(null);
  const [agentConfig, setAgentConfig] = useState<AgentPluginConfig | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Intent patterns state
  const [intentConfig, setIntentConfig] = useState<IntentConfig | null>(null);
  const [intentPatternsDirty, setIntentPatternsDirty] = useState(false);
  const [savingPatterns, setSavingPatterns] = useState(false);

  // Staleness state
  const [stale, setStale] = useState(false);
  const [stalenessGeneratedAt, setStalenessGeneratedAt] = useState<string | null>(null);
  const [stalenessLastContextChange, setStalenessLastContextChange] = useState<string | null>(null);

  // Regeneration state
  const [regenerating, setRegenerating] = useState(false);
  const [generatedConfig, setGeneratedConfig] = useState<IntentConfig | null>(null);
  const [showDiffView, setShowDiffView] = useState(false);
  const [acceptingPatterns, setAcceptingPatterns] = useState(false);
  const [regenerateError, setRegenerateError] = useState('');

  // Load agent info
  const loadAgent = useCallback(async () => {
    if (!agentId || !token) return;
    try {
      const agents = await apiGet<Agent[]>('/api/console/agents', token);
      const found = agents.find((a) => a.id === agentId);
      if (found) {
        setAgent(found);
      }
    } catch (e) {
      console.error('Failed to load agent:', e);
    }
  }, [agentId, token]);

  // Load plugin details and configuration
  const loadPluginConfig = useCallback(async () => {
    if (!agentId || !pluginId || !token) return;
    setLoading(true);
    setLoadError('');
    try {
      const data = await apiGet<PluginDetailResponse>(
        `/api/agents/${agentId}/plugins/${pluginId}`,
        token
      );

      setPlugin(data.plugin);
      setAgentConfig(data.agentConfig);

      // Initialize form values with existing config or defaults
      const schema = data.plugin.configSchema as JSONSchema7 | undefined;
      const existingConfig = (data.agentConfig?.config as Record<string, unknown>) || {};

      if (schema) {
        const schemaFields = pickFormValuesFromSchema(existingConfig, schema);
        const valuesWithDefaults = applyDefaults(schemaFields, schema);
        setFormValues(valuesWithDefaults);
      } else {
        setFormValues(existingConfig);
      }

      // Load intent patterns if present in config
      const rawConfig = data.agentConfig?.config as Record<string, unknown> | undefined;
      if (rawConfig?.intentPatterns && pluginId && pluginIdSupportsIntentPatternsUi(pluginId)) {
        setIntentConfig(rawConfig.intentPatterns as IntentConfig);
      } else {
        setIntentConfig(null);
      }

      setHasChanges(false);
      setIntentPatternsDirty(false);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [agentId, pluginId, token]);

  useEffect(() => {
    void loadAgent();
    void loadPluginConfig();
  }, [loadAgent, loadPluginConfig]);

  // Check staleness on load
  const checkStaleness = useCallback(async () => {
    if (!agentId || !pluginId || !token) return;
    try {
      const result = await apiGet<{
        stale: boolean;
        generatedAt: string | null;
        lastContextChange: string | null;
      }>(`/api/agents/${agentId}/plugins/${pluginId}/pattern-staleness`, token);
      setStale(result.stale);
      setStalenessGeneratedAt(result.generatedAt);
      setStalenessLastContextChange(result.lastContextChange);
    } catch (e) {
      // Staleness check is non-critical; silently ignore errors
      console.warn('Failed to check pattern staleness:', e);
    }
  }, [agentId, pluginId, token]);

  // Check staleness after plugin config loads
  useEffect(() => {
    if (!loading && intentConfig) {
      void checkStaleness();
    }
  }, [loading, intentConfig, checkStaleness]);

  // Handle regenerate patterns
  const handleRegeneratePatterns = async () => {
    if (!agentId || !pluginId || !token) return;

    setRegenerating(true);
    setRegenerateError('');
    try {
      const result = await apiPost<GeneratedIntentConfigResponse>(
        `/api/agents/${agentId}/plugins/${pluginId}/generate-patterns`,
        {},
        token
      );

      // Convert from GeneratedIntentConfig (intentPatterns key) to IntentConfig (patterns key)
      const converted = toIntentConfig(result);

      if (intentConfig) {
        // If patterns already exist, show diff for review
        setGeneratedConfig(converted);
        setShowDiffView(true);
      } else {
        // First-time generation: save directly and reload
        await apiPut(
          `/api/agents/${agentId}/plugins/${pluginId}/intent-patterns`,
          { intentPatterns: converted },
          token,
          { successMessage: 'Detección de intenciones generada correctamente' }
        );
        setStale(false);
        setStalenessGeneratedAt(converted.generatedAt);
        await loadPluginConfig();
      }
    } catch (e) {
      setRegenerateError((e as Error).message || 'Error al regenerar patrones');
    } finally {
      setRegenerating(false);
    }
  };

  // Handle accept regenerated patterns
  const handleAcceptPatterns = async () => {
    if (!agentId || !pluginId || !token || !generatedConfig) return;

    setAcceptingPatterns(true);
    try {
      await apiPut(
        `/api/agents/${agentId}/plugins/${pluginId}/intent-patterns`,
        { intentPatterns: generatedConfig },
        token,
        { successMessage: 'Patrones regenerados guardados correctamente' }
      );
      setShowDiffView(false);
      setGeneratedConfig(null);
      // Clear staleness after successful regeneration
      setStale(false);
      setStalenessGeneratedAt(generatedConfig.generatedAt);
      // Reload config to reflect changes
      await loadPluginConfig();
    } catch (e) {
      setRegenerateError((e as Error).message || 'Error al guardar patrones');
    } finally {
      setAcceptingPatterns(false);
    }
  };

  // Handle reject regenerated patterns
  const handleRejectPatterns = () => {
    setShowDiffView(false);
    setGeneratedConfig(null);
  };

  // Handle form value changes
  const handleFormChange = useCallback((newValues: Record<string, unknown>) => {
    setFormValues(newValues);
    setHasChanges(true);
    // Clear errors when user makes changes
    setErrors([]);
  }, []);

  // Handle save
  const handleSave = async () => {
    if (!agentId || !pluginId || !token || !plugin) return;

    // Validate locally first
    const schema = plugin.configSchema as JSONSchema7 | undefined;
    if (schema) {
      const validationErrors = validateForm(formValues, schema);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }
    }

    setSaving(true);
    setErrors([]);

    try {
      await apiPut(
        `/api/agents/${agentId}/plugins/${pluginId}`,
        { config: formValues },
        token,
        { successMessage: 'Configuración guardada correctamente' }
      );
      setHasChanges(false);
      // Reload to get updated config
      await loadPluginConfig();
    } catch (e) {
      const error = e as Error & { details?: FieldError[] };
      // Check if the error contains validation details
      if (error.details && Array.isArray(error.details)) {
        setErrors(error.details);
      } else {
        setErrors([{ path: '', message: error.message }]);
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(`/app/agents/${agentId}/plugins`);
  };

  // Handle intent patterns change
  const handleIntentConfigChange = useCallback((newConfig: IntentConfig) => {
    setIntentConfig(newConfig);
    setIntentPatternsDirty(true);
  }, []);

  // Handle save intent patterns
  const handleSavePatterns = async () => {
    if (!agentId || !pluginId || !token || !intentConfig) return;

    setSavingPatterns(true);
    try {
      await apiPut(
        `/api/agents/${agentId}/plugins/${pluginId}/intent-patterns`,
        { intentPatterns: intentConfig },
        token,
        { successMessage: 'Detección de intenciones guardada correctamente' }
      );
      setIntentPatternsDirty(false);
      // Reload to get updated config
      await loadPluginConfig();
    } catch (e) {
      const error = e as Error;
      setErrors([{ path: '', message: error.message }]);
    } finally {
      setSavingPatterns(false);
    }
  };

  // Determine if plugin supports intent patterns (calendar, s3, or already has patterns in config)
  const supportsIntentPatterns = useMemo(() => {
    if (intentConfig) return true;
    if (!pluginId) return false;
    return pluginIdSupportsIntentPatternsUi(pluginId);
  }, [intentConfig, pluginId]);

  // Redirect if not authenticated
  if (!token) {
    navigate('/login', { replace: true });
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 48,
            color: tokens.colors.neutral[500],
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ animation: 'spin 1s linear infinite', marginRight: 12 }}
          >
            <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
          </svg>
          Cargando configuración...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
        <Link
          to={`/app/agents/${agentId}/plugins`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: tokens.colors.neutral[600],
            textDecoration: 'none',
            fontSize: 14,
            marginBottom: 24,
          }}
        >
          <IconArrowLeft />
          Volver a plugins
        </Link>
        <Card variant="outlined" padding="lg">
          <div
            style={{
              textAlign: 'center',
              color: tokens.colors.error.dark,
            }}
          >
            <p style={{ margin: 0 }}>{loadError}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void loadPluginConfig()}
              style={{ marginTop: 16 }}
            >
              Reintentar
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Plugin not found
  if (!plugin) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
        <Link
          to={`/app/agents/${agentId}/plugins`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: tokens.colors.neutral[600],
            textDecoration: 'none',
            fontSize: 14,
            marginBottom: 24,
          }}
        >
          <IconArrowLeft />
          Volver a plugins
        </Link>
        <Card variant="outlined" padding="lg">
          <div style={{ textAlign: 'center', color: tokens.colors.neutral[500] }}>
            <p style={{ margin: 0 }}>Plugin no encontrado</p>
          </div>
        </Card>
      </div>
    );
  }

  const schema = plugin.configSchema as JSONSchema7 | undefined;
  const hasSchema = schema && schema.properties && Object.keys(schema.properties).length > 0;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
      {/* Header with back navigation */}
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/app/agents/${agentId}/plugins`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: tokens.colors.neutral[600],
            textDecoration: 'none',
            fontSize: 14,
            marginBottom: 12,
            transition: `color ${tokens.transitions.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = tokens.colors.primary[600])}
          onMouseLeave={(e) => (e.currentTarget.style.color = tokens.colors.neutral[600])}
        >
          <IconArrowLeft />
          Volver a plugins
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: tokens.borderRadius.lg,
              backgroundColor: tokens.colors.primary[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: tokens.colors.primary[600],
            }}
          >
            <IconSettings />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h1 style={{ margin: 0, fontSize: 22, color: tokens.colors.neutral[900] }}>
                Configurar {plugin.name}
              </h1>
              <Badge variant={agentConfig?.isActive ? 'success' : 'neutral'} size="sm">
                {agentConfig?.isActive ? 'Activo' : 'Inactivo'}
              </Badge>
            </div>
            {agent && (
              <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[500] }}>
                Agente: <strong>{agent.name}</strong>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Plugin description */}
      <Card variant="outlined" padding="md" style={{ marginBottom: 24 }}>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: tokens.colors.neutral[600],
            lineHeight: 1.5,
          }}
        >
          {plugin.description}
        </p>
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            color: tokens.colors.neutral[400],
          }}
        >
          <span>Versión {plugin.version}</span>
          <span>•</span>
          <span style={{ textTransform: 'capitalize' }}>{plugin.category}</span>
        </div>
      </Card>

      {/* Calendar-specific navigation links */}
      {pluginId === 'calendar' && (
        <Card variant="outlined" padding="md" style={{ marginBottom: 24 }}>
          <h3
            style={{
              margin: 0,
              marginBottom: 12,
              fontSize: tokens.typography.fontSize.base,
              fontWeight: tokens.typography.fontWeight.semibold,
              color: tokens.colors.neutral[800],
            }}
          >
            Gestión del Calendario
          </h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link
              to={`/app/agents/${agentId}/calendar/resource-types`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 500,
                color: tokens.colors.primary[700],
                textDecoration: 'none',
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${tokens.colors.primary[200]}`,
                backgroundColor: tokens.colors.primary[50],
                transition: `background-color ${tokens.transitions.fast}`,
              }}
            >
              🏷️ Tipos de recurso
            </Link>
            <Link
              to={`/app/agents/${agentId}/calendar/resources`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 500,
                color: tokens.colors.primary[700],
                textDecoration: 'none',
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${tokens.colors.primary[200]}`,
                backgroundColor: tokens.colors.primary[50],
                transition: `background-color ${tokens.transitions.fast}`,
              }}
            >
              🏠 Recursos
            </Link>
            <Link
              to={`/app/agents/${agentId}/calendar/bookings`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 500,
                color: tokens.colors.primary[700],
                textDecoration: 'none',
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${tokens.colors.primary[200]}`,
                backgroundColor: tokens.colors.primary[50],
                transition: `background-color ${tokens.transitions.fast}`,
              }}
            >
              📅 Reservas
            </Link>
          </div>
        </Card>
      )}

      {pluginId === 'impacto-intelligence' && agentId && (
        <ImpactoIntelligencePanel agentId={agentId} token={token} />
      )}

      {pluginId === 'lead-qualification' && formValues.mode !== 'internal' && (
        <Card variant="outlined" padding="md" style={{ marginBottom: 24 }}>
          <h3
            style={{
              margin: 0,
              marginBottom: 12,
              fontSize: tokens.typography.fontSize.base,
              fontWeight: tokens.typography.fontWeight.semibold,
              color: tokens.colors.neutral[800],
            }}
          >
            Destinos
          </h3>
          <Link
            to="/app/conectores-ads"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 500,
              color: tokens.colors.primary[700],
              textDecoration: 'none',
              borderRadius: tokens.borderRadius.md,
              border: `1px solid ${tokens.colors.primary[200]}`,
              backgroundColor: tokens.colors.primary[50],
            }}
          >
            Conectores de Ads
          </Link>
        </Card>
      )}

      {/* General error message */}
      {errors.some((e) => e.path === '') && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {errors.find((e) => e.path === '')?.message}
        </div>
      )}

      {/* Configuration form */}
      <Card variant="elevated" padding="lg">
        <h2
          style={{
            margin: 0,
            marginBottom: 20,
            fontSize: 16,
            fontWeight: 600,
            color: tokens.colors.neutral[900],
          }}
        >
          Configuración
        </h2>

        {hasSchema ? (
          <>
            {pluginId === 'lead-qualification' && agentId ? (
              <LeadQualificationSettings
                agentId={agentId}
                token={token}
                values={formValues}
                disabled={saving}
                onChange={handleFormChange}
              />
            ) : (
              <DynamicSchemaForm
                schema={schema}
                values={formValues}
                onChange={handleFormChange}
                errors={errors.filter((e) => e.path !== '')}
                disabled={saving}
              />
            )}

            {/* Action buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                marginTop: 24,
                paddingTop: 20,
                borderTop: `1px solid ${tokens.colors.neutral[200]}`,
              }}
            >
              <Button variant="ghost" onClick={handleCancel} disabled={saving}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                disabled={!hasChanges}
              >
                Guardar configuración
              </Button>
            </div>
          </>
        ) : (
          <div
            style={{
              padding: tokens.spacing[4],
              textAlign: 'center',
              color: tokens.colors.neutral[500],
              fontSize: tokens.typography.fontSize.sm,
            }}
          >
            Este plugin no requiere configuración adicional.
          </div>
        )}
      </Card>

      {/* Intent Pattern Editor - shown for plugins that support intent patterns */}
      {supportsIntentPatterns && (
        <Card variant="elevated" padding="lg" style={{ marginTop: 24 }}>
          {/* Staleness indicator */}
          {intentConfig && (
            <StalenessIndicator
              stale={stale}
              generatedAt={stalenessGeneratedAt}
              lastContextChange={stalenessLastContextChange}
            />
          )}

          {/* Regenerate error message */}
          {regenerateError && (
            <div
              style={{
                padding: '12px 16px',
                marginBottom: 16,
                borderRadius: tokens.borderRadius.md,
                backgroundColor: tokens.colors.error.light,
                color: tokens.colors.error.dark,
                fontSize: 14,
              }}
              role="alert"
            >
              {regenerateError}
            </div>
          )}

          {/* Header with regenerate button */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: tokens.spacing[4],
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: tokens.typography.fontSize.lg,
                fontWeight: tokens.typography.fontWeight.semibold,
                color: tokens.colors.neutral[900],
              }}
            >
              Detección de intenciones
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRegeneratePatterns}
              loading={regenerating}
              disabled={regenerating || savingPatterns}
              aria-label="Regenerar detección de intenciones"
            >
              {intentConfig ? 'Regenerar' : 'Generar automáticamente'}
            </Button>
          </div>

          {intentConfig ? (
            <>
              <PatternEditor
                intentConfig={intentConfig}
                onChange={handleIntentConfigChange}
                disabled={savingPatterns || regenerating}
              />

              {/* Save patterns button */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 12,
                  marginTop: 24,
                  paddingTop: 20,
                  borderTop: `1px solid ${tokens.colors.neutral[200]}`,
                }}
              >
                <Button
                  variant="primary"
                  onClick={handleSavePatterns}
                  loading={savingPatterns}
                  disabled={!intentPatternsDirty}
                >
                  Guardar cambios
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{
                padding: tokens.spacing[6],
                textAlign: 'center',
                color: tokens.colors.neutral[500],
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.normal,
              }}
            >
              <p style={{ margin: 0, marginBottom: tokens.spacing[3] }}>
                Aún no se ha configurado cómo el plugin detecta las intenciones del usuario.
              </p>
              <p style={{ margin: 0, color: tokens.colors.neutral[400], fontSize: tokens.typography.fontSize.xs }}>
                Haz clic en "Generar automáticamente" para que el sistema analice el contexto del agente y cree las reglas de detección, o espera a que se generen al activar el plugin.
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Pattern Diff View Modal */}
      {intentConfig && generatedConfig && (
        <PatternDiffView
          isOpen={showDiffView}
          currentConfig={intentConfig}
          generatedConfig={generatedConfig}
          onAccept={handleAcceptPatterns}
          onReject={handleRejectPatterns}
          accepting={acceptingPatterns}
        />
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PluginConfigPage;
